import { ITicketStatusCount, TICKET_STATUSES } from "@src/types/statusCheck";

export interface TicketStatusRow {
  /** Raw `ticket_status` value, or `"__null__"` for the NULL group. */
  key: string;
  label: string;
  count: number;
  /** False for a value not in the schema enum (or NULL) — rendered but flagged. */
  known: boolean;
}

const NULL_KEY = "__null__";

const toLabel = (status: string): string =>
  status
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Turns the endpoint's `GROUP BY` rows into the rows the page renders: every status
 * in the schema enum first (zero-filled, so "none" reads as `0` instead of a missing
 * card), then anything the response carried that the enum does not — a NULL group or
 * a value added to the schema but not to `TICKET_STATUSES`.
 */
export const buildTicketStatusRows = (
  counts: ITicketStatusCount[] = []
): TicketStatusRow[] => {
  const byStatus = new Map<string, number>();
  counts.forEach(({ ticketStatus, count }) => {
    const key = ticketStatus ?? NULL_KEY;
    byStatus.set(key, (byStatus.get(key) ?? 0) + count);
  });

  const known: TicketStatusRow[] = TICKET_STATUSES.map((status) => ({
    key: status,
    label: toLabel(status),
    count: byStatus.get(status) ?? 0,
    known: true,
  }));

  const extra: TicketStatusRow[] = [...byStatus.entries()]
    .filter(([key]) => !(TICKET_STATUSES as readonly string[]).includes(key))
    .map(([key, count]) => ({
      key,
      label: key === NULL_KEY ? "No status (NULL)" : toLabel(key),
      count,
      known: false,
    }));

  return [...known, ...extra];
};
